<template>
  <div>
    <basic-container>
      <div class="partner-manage-container">
        <!-- 搜索栏 -->
        <PartnerSearch 
          ref="searchForm" 
          @search="handleSearch" 
          @add="handleAdd"
          :searchForm="searchForm"
          :loading="loading"
        />
        
        <!-- 合伙人列表 -->
        <div class="partner-table-container">
          <PartnerTable 
            ref="partnerTable" 
            :tableData="partnerData" 
            :loading="loading"
            @edit="handleEdit"
            @delete="handleDelete"
            @log="handleLog"
          />
        </div>

        <!-- 分页组件 -->
        <div class="pagination-container">
          <ElementPagination
            :current-page="page.current"
            :page-size="page.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="page.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>

      <!-- 新增/编辑弹窗 -->
      <el-dialog
        :title="componentProps.type === 'add' ? '新增合伙人' : '编辑合伙人'"
        :visible.sync="editDialogVisible"
        width="600px"
        @close="handleEditDialogClose"
        append-to-body
      >
        <PartnerEdit
          ref="partnerEdit"
          :type="componentProps.type"
          :id="componentProps.id"
          :formData="editFormData"
          @add="saveAddPartner"
          @edit="saveEditPartner"
        />
        <span slot="footer" class="dialog-footer">
          <el-button @click="handleEditDialogClose">取消</el-button>
          <el-button type="primary" @click="handleEditSubmit">保存</el-button>
        </span>
      </el-dialog>

      <!-- 日志弹窗 -->
      <OptionLog 
        ref="optionLog" 
        :columnConfig="optionLogColumn" 
        @reload="reloadLog"
        :closeBtn="false"
        title="操作日志"
      >
        <template #type="{ row }">
          <div>
            <span v-if="row.type === 1">新增</span>
            <span v-if="row.type === 2">修改</span>
            <span v-if="row.type === 3">删除</span>
            <span v-if="row.type === 4">上线</span>
            <span v-if="row.type === 5">下线</span>
          </div>
        </template>
      </OptionLog>
    </basic-container>
  </div>
</template>

<script>
import PartnerSearch from "./components/search.vue";
import PartnerTable from "./components/partnerTable.vue";
import PartnerEdit from './components/PartnerEdit.vue';
import ElementPagination from '@/components/ElementPagination';
import OptionLog from '@/components/OptionsLog/optionLog.vue';
import { getLogList } from '@/api/tv/log';
import { getPartnerList, addPartner, editPartner, deletePartner } from "@/api/markManage/partnerManage";
import { cloneDeep } from 'lodash';

export default {
  name: "MarkPartnerManage",
  components: {
    PartnerSearch,
    PartnerTable,
    PartnerEdit,
    ElementPagination,
    OptionLog
  },
  data() {
    return {
      loading: false,
      partnerData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      // 搜索表单
      searchForm: {
        keyword: "", // 合伙人名称&账号
        channelId: "" // 关联渠道（单选）
      },
      // 编辑弹窗显示状态
      editDialogVisible: false,
      // 组件属性
      componentProps: {
        type: 'add', // 'add' 或 'edit'
        id: ''
      },
      editFormData: {
        partnerName: '',
        partnerPhone: '',
        remark: ''
      },
      // 日志列配置
      optionLogColumn: [
        {
          prop: "operateTime",
          label: "操作时间",
        },
        {
          prop: "operator",
          label: "操作人",
        },
        {
          prop: "type",
          label: "操作类型",
          slot: "type",
        },
      ],
    };
  },
  created() {
    this.getPartnerList();
  },
  methods: {
    // 获取合伙人列表
    async getPartnerList() {
      const requestParams = {
        current: this.page.current,
        size: this.page.size,
        keyword: this.searchForm.keyword,
        channelId: this.searchForm.channelId || ''
      };
      
      this.loading = true;
      try {
        const { data } = await getPartnerList(requestParams);

        if(data.code === 200) {
          const list = data.data.records || data.data || [];
          
          this.page.total = data.data.total || 0;
          // 处理边界情况：如果总数大于0但当前页无数据，且当前页不是第一页，则返回上一页
          if (this.page.total > 0 && list.length === 0 && this.page.current > 1) {
            this.page.current = this.page.current - 1;
            this.getPartnerList();
            return;
          }

          this.partnerData = list.map(item => {
            return {
              id: item.id,
              partnerName: item.partnerName || '',
              partnerPhone: item.partnerPhone || '',
              channelId: item.channelId || '',
              channelName: item.channelName || '',
              pendingAmount: item.pendingAmount || 0,
              settledAmount: item.settledAmount || 0,
              totalAmount: item.totalAmount || 0,
              promotionUrl: item.promotionUrl || '',
              remark: item.remark || '',
              ...item
            };
          });
        } else {
          this.$message.error(data.msg || '获取列表失败');
        }
      } catch (error) {
        this.$message.error('获取列表失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 搜索处理
    handleSearch(params) {
      this.page.current = 1;
      this.searchForm = params;
      this.getPartnerList();
    },
    
    // 新增处理
    handleAdd() {
      this.componentProps = {
        type: 'add',
        id: ''
      };
      this.resetEditForm();
      this.editDialogVisible = true;
      // 清除表单验证状态
      this.$nextTick(() => {
        if (this.$refs.partnerEdit) {
          this.$refs.partnerEdit.clearValidate();
        }
      });
    },
    
    // 编辑处理
    handleEdit(row) {
      this.componentProps = {
        type: 'edit',
        id: row.id
      };
      // 接口返回字段：partnerName 和 partnerPhone
      this.editFormData = {
        partnerName: row.partnerName || '',
        partnerPhone: row.partnerPhone || '',
        remark: row.remark || '',
        channelId: row.channelId || '',
        channelName: row.channelName || ''
      };
      this.editDialogVisible = true;
      // 清除表单验证状态
      this.$nextTick(() => {
        if (this.$refs.partnerEdit) {
          this.$refs.partnerEdit.clearValidate();
        }
      });
    },

    // 编辑弹窗提交
    handleEditSubmit() {
      this.$refs.partnerEdit.handleSubmit();
    },

    // 编辑弹窗关闭
    handleEditDialogClose() {
      this.editDialogVisible = false;
      this.resetEditForm();
      // 清除表单验证状态
      this.$nextTick(() => {
        if (this.$refs.partnerEdit) {
          this.$refs.partnerEdit.clearValidate();
        }
      });
    },

    // 删除处理
    async handleDelete(row) {
      // 检查是否有待结算金额
      if (row.pendingAmount && parseFloat(row.pendingAmount) > 0) {
        this.$message.warning('有待结算金额，无法删除');
        return;
      }

      try {
        await this.$confirm('确认删除该合伙人吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        const { data } = await deletePartner({ id: row.id });
        
        if (data.code === 200) {
          this.$message.success('删除成功');
          this.getPartnerList();
        } else {
          this.$message.error(data.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败');
        }
      }
    },

    // 日志处理
    async handleLog(row) {
      try {
        const optSearch = this.$refs.optionLog.searchParams;
        const { data } = await getLogList({
          ...optSearch,
          subjectId: row.id,
          module: 'PARTNER'
        });
        if (data.code === 200) {
          const list = cloneDeep(data.data.records || []);
          optSearch.total = data.data.total || 0;
          this.$refs.optionLog.show(list, row.id);
        } else {
          this.$message.error(data.msg || '获取日志失败');
        }
      } catch (error) {
        this.$message.error('获取日志失败');
      }
    },

    // 日志分页
    async reloadLog(id) {
      try {
        const optSearch = this.$refs.optionLog.searchParams;
        const { data } = await getLogList({
          ...optSearch,
          subjectId: id,
          module: 'PARTNER'
        });
        if (data.code === 200) {
          const list = cloneDeep(data.data.records || []);
          this.$refs.optionLog.list = list;
          this.$refs.optionLog.searchParams.total = data.data.total || 0;
        } else {
          this.$message.error(data.msg || '获取日志失败');
        }
      } catch (error) {
        this.$message.error('获取日志失败');
      }
    },


    // 新增合伙人
    async saveAddPartner(form) {
      try {
        // 接口参数：partnerName, partnerPhone, remark
        const params = {
          partnerName: (form.partnerName || '').trim(),
          partnerPhone: (form.partnerPhone || '').trim(),
          remark: (form.remark || '').trim()
        };
        const { data } = await addPartner(params);
        if (data.code === 200) {
          this.$message.success('新增成功');
          this.editDialogVisible = false;
          this.resetEditForm();
          this.getPartnerList();
        } else {
          this.$message.error(data.msg || '新增失败');
        }
      } catch (error) {
        this.$message.error('新增合伙人失败');
      }
    },

    // 编辑合伙人
    async saveEditPartner(form) {
      try {
        // 接口参数：id（必填）, partnerName, partnerPhone, remark
        const params = {
          id: form.id,
          partnerName: (form.partnerName || '').trim(),
          partnerPhone: (form.partnerPhone || '').trim(),
          remark: (form.remark || '').trim()
        };
        const { data } = await editPartner(params);
        if (data.code === 200) {
          this.$message.success('编辑成功');
          this.editDialogVisible = false;
          this.resetEditForm();
          this.getPartnerList();
        } else {
          this.$message.error(data.msg || '编辑失败');
        }
      } catch (error) {
        this.$message.error('编辑合伙人失败');
      }
    },

    // 重置编辑表单
    resetEditForm() {
      this.editFormData = {
        partnerName: '',
        partnerPhone: '',
        remark: ''
      };
    },

    // 分页处理
    handleSizeChange(val) {
      this.page.size = val;
      this.page.current = 1;
      this.getPartnerList();
    },

    handleCurrentChange(val) {
      this.page.current = val;
      this.getPartnerList();
    }
  }
};
</script>

<style lang="scss" scoped>
.partner-manage-container {
  .partner-table-container {
    margin: 20px 0;
  }

  .pagination-container {
    margin-top: 20px;
  }
}
</style>

